/**
 * Workflows v2 — trigger-window contract (`{{range_start}}` / `{{range_end}}`).
 *
 * Pure window-computation + fire-instant helpers for recurring workflows. NO database, NO wall
 * clock except the `from`/`now`/slot arguments the caller passes in — every function is
 * deterministic and table-testable.
 *
 * A recurring workflow runs over the half-open window `[range_start, range_end)`:
 *   range_start = the previous schedule slot's instant (inclusive)
 *   range_end   = this schedule slot's instant            (exclusive)
 *
 * v0 supports a CONSTRAINED cadence set (`hourly | daily | weekdays | weekly | monthly` + `HH:mm`
 * + IANA tz), resolved with host-timezone-independent `Intl`. We do NOT add a cron-parser library: the
 * persisted 5-field `trigger.cron` string (produced by the frontend `cadenceToCron`,
 * `ui/src/workflow/types.ts`) is parsed BACK into this finite cadence enum by `parseCron`.
 *
 * The scheduler (part 5) reads the previous slot from the durable ledger and is the authority for
 * `range_start` of a live run; `prevFire`/`nextFire` here are used to (a) decide which slot to
 * insert next and (b) synthesize the first-run / manual lower bound. DST is resolved once, here,
 * at fire-instant computation — the binder only ever sees absolute instants.
 */
export type Cadence = 'hourly' | 'daily' | 'weekdays' | 'weekly' | 'monthly';

/** Fully-resolved recurrence: cadence + wall-clock HH:mm + IANA zone. */
export interface RecurringSpec {
  cadence: Cadence;
  /** 0..23. Ignored for `hourly`. */
  hour: number;
  /** 0..59. */
  minute: number;
  /** IANA timezone, e.g. `America/New_York`. */
  timezone: string;
}

export interface TriggerWindow {
  /** range_start — inclusive lower bound. */
  start: Date;
  /** range_end — exclusive upper bound. */
  end: Date;
}

/**
 * Exact-match cron shapes emitted by the frontend `cadenceToCron`. Each entry's `match` is a
 * predicate over the 5 cron fields `[minute, hour, dom, month, dow]`; the `cadence` is the enum it
 * maps back to. Order matters only in that `hourly` (fixed `0 * * * *`) is checked like the rest —
 * every shape is mutually exclusive so there is no ambiguity.
 *
 * Mirror of `cadenceToCron` (ui/src/workflow/types.ts) — KEEP IN SYNC:
 *   hourly   -> '0 * * * *'        (minute fixed to 0, hour wild)
 *   daily    -> 'm h * * *'
 *   weekdays -> 'm h * * 1-5'
 *   weekly   -> 'm h * * 1'        (Monday)
 *   monthly  -> 'm h 1 * *'        (1st of month)
 */
interface CronShape {
  cadence: Cadence;
  /** @param f the 5 cron fields. @returns parsed hour/minute, or null if this shape doesn't match. */
  match: (f: readonly string[]) => { hour: number; minute: number } | null;
}

/** Parse an integer field that must be exactly an integer in `[lo, hi]`; null otherwise. */
function intField(field: string | undefined, lo: number, hi: number): number | null {
  if (field === undefined || !/^\d+$/.test(field)) return null;
  const n = Number(field);
  return n >= lo && n <= hi ? n : null;
}

const CRON_SHAPES: CronShape[] = [
  {
    cadence: 'hourly',
    // '0 * * * *' — minute fixed at 0, everything else wild. hour is irrelevant (returned as 0).
    match: ([m, h, dom, mon, dow]) =>
      m === '0' && h === '*' && dom === '*' && mon === '*' && dow === '*' ? { hour: 0, minute: 0 } : null,
  },
  {
    cadence: 'weekdays',
    // 'm h * * 1-5'
    match: ([m, h, dom, mon, dow]) => parseHm(m, h, dom === '*' && mon === '*' && dow === '1-5'),
  },
  {
    cadence: 'weekly',
    // 'm h * * 1' (Monday)
    match: ([m, h, dom, mon, dow]) => parseHm(m, h, dom === '*' && mon === '*' && dow === '1'),
  },
  {
    cadence: 'monthly',
    // 'm h 1 * *' (1st of month)
    match: ([m, h, dom, mon, dow]) => parseHm(m, h, dom === '1' && mon === '*' && dow === '*'),
  },
  {
    cadence: 'daily',
    // 'm h * * *'
    match: ([m, h, dom, mon, dow]) => parseHm(m, h, dom === '*' && mon === '*' && dow === '*'),
  },
];

/** Shared helper: validate `m`/`h` as integers and gate on the cadence's field-shape predicate. */
function parseHm(
  m: string | undefined,
  h: string | undefined,
  shapeOk: boolean,
): { hour: number; minute: number } | null {
  if (!shapeOk) return null;
  const minute = intField(m, 0, 59);
  const hour = intField(h, 0, 23);
  if (minute === null || hour === null) return null;
  return { hour, minute };
}

/**
 * Parse the persisted 5-field cron string back into the constrained cadence set.
 * Throws on anything outside the finite set (surfaced at save-time validation, part 8).
 */
export function parseCron(cron: string): { cadence: Cadence; hour: number; minute: number } {
  const fields = cron.trim().split(/\s+/);
  if (fields.length !== 5) {
    throw new Error(`unsupported cadence: expected a 5-field cron, got "${cron}"`);
  }
  for (const shape of CRON_SHAPES) {
    const hm = shape.match(fields);
    if (hm) return { cadence: shape.cadence, hour: hm.hour, minute: hm.minute };
  }
  throw new Error(`unsupported cadence: "${cron}" is not one of hourly/daily/weekdays/weekly/monthly`);
}

/** Combine a parsed cron + the trigger's IANA tz into a RecurringSpec. */
export function toRecurringSpec(trigger: { cron: string; timezone: string }): RecurringSpec {
  const { cadence, hour, minute } = parseCron(trigger.cron);
  return { cadence, hour, minute, timezone: trigger.timezone };
}

// ─── Fire-instant computation (DST-correct, host-timezone-independent Intl) ───

/** Monday=1 .. Friday=5 per cron `dow` convention (Intl uses Sun=0..Sat=6 below). */
const MONDAY = 1;
const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

interface LocalDateParts {
  year: number;
  month0: number;
  day: number;
}

interface LocalDateTimeParts extends LocalDateParts {
  hour: number;
  minute: number;
  second: number;
}

const LOCAL_PART_FORMATTERS = new Map<string, Intl.DateTimeFormat>();

function localPartFormatter(zone: string): Intl.DateTimeFormat {
  const cached = LOCAL_PART_FORMATTERS.get(zone);
  if (cached) return cached;
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    hourCycle: 'h23',
  });
  LOCAL_PART_FORMATTERS.set(zone, formatter);
  return formatter;
}

function localParts(date: Date, zone: string): LocalDateTimeParts {
  let year: number | undefined;
  let month: number | undefined;
  let day: number | undefined;
  let hour: number | undefined;
  let minute: number | undefined;
  let second: number | undefined;

  for (const part of localPartFormatter(zone).formatToParts(date)) {
    switch (part.type) {
      case 'year':
        year = Number(part.value);
        break;
      case 'month':
        month = Number(part.value);
        break;
      case 'day':
        day = Number(part.value);
        break;
      case 'hour':
        hour = Number(part.value);
        break;
      case 'minute':
        minute = Number(part.value);
        break;
      case 'second':
        second = Number(part.value);
        break;
      case 'unknown':
      case 'literal':
      case 'dayPeriod':
      case 'era':
      case 'timeZoneName':
      case 'weekday':
      case 'fractionalSecond':
        break;
    }
  }

  if (
    year === undefined ||
    month === undefined ||
    day === undefined ||
    hour === undefined ||
    minute === undefined ||
    second === undefined
  ) {
    throw new Error(`could not resolve local time parts for ${zone}`);
  }

  return { year, month0: month - 1, day, hour: hour === 24 ? 0 : hour, minute, second };
}

function offsetMinutes(date: Date, zone: string): number {
  const local = localParts(date, zone);
  return (
    (Date.UTC(local.year, local.month0, local.day, local.hour, local.minute, local.second) - date.getTime()) / MINUTE_MS
  );
}

function localMinuteOfDay(parts: Pick<LocalDateTimeParts, 'hour' | 'minute'>): number {
  return parts.hour * 60 + parts.minute;
}

function localWallMinute(parts: LocalDateParts & Pick<LocalDateTimeParts, 'hour' | 'minute'>): number {
  return Date.UTC(parts.year, parts.month0, parts.day) / MINUTE_MS + localMinuteOfDay(parts);
}

interface ResolvedWallTime {
  /** Exact wall-clock matches, sorted by absolute instant. Folds have two entries. */
  exactInstants: number[];
  /** First valid instant after a spring-forward gap, or null when the wall-clock exists. */
  postGapInstant: number | null;
}

function wallTimeDescription(
  year: number,
  month0: number,
  day: number,
  hour: number,
  minute: number,
  zone: string,
): string {
  return `${pad4(year)}-${pad2(month0 + 1)}-${pad2(day)} ${pad2(hour)}:${pad2(minute)}:00 in ${zone}`;
}

function noValidInstantError(
  year: number,
  month0: number,
  day: number,
  hour: number,
  minute: number,
  zone: string,
): Error {
  return new Error(
    `materialize: no valid instant found for ${wallTimeDescription(year, month0, day, hour, minute, zone)}`,
  );
}

function resolveWallTime(
  year: number,
  month0: number,
  day: number,
  hour: number,
  minute: number,
  zone: string,
): ResolvedWallTime {
  const targetUtc = Date.UTC(year, month0, day, hour, minute, 0, 0);
  const sampleOffsets = [-DAY_MS, -12 * HOUR_MS, 0, 12 * HOUR_MS, DAY_MS].map((delta) =>
    offsetMinutes(new Date(targetUtc + delta), zone),
  );
  const targetWallMinute = localWallMinute({ year, month0, day, hour, minute });
  const exactInstants: number[] = [];
  const postGapMatches: { instant: number; wallMinute: number }[] = [];

  for (const offset of new Set(sampleOffsets)) {
    const instant = targetUtc - offset * MINUTE_MS;
    const local = localParts(new Date(instant), zone);
    if (local.second !== 0) continue;

    const candidateWallMinute = localWallMinute(local);
    if (candidateWallMinute === targetWallMinute) {
      exactInstants.push(instant);
    } else if (candidateWallMinute > targetWallMinute) {
      postGapMatches.push({ instant, wallMinute: candidateWallMinute });
    }
  }

  exactInstants.sort((a, b) => a - b);
  const [firstPostGapMatch, ...remainingPostGapMatches] = postGapMatches;
  const postGapInstant =
    firstPostGapMatch === undefined
      ? null
      : remainingPostGapMatches.reduce(
          (currentBest, candidate) =>
            candidate.wallMinute < currentBest.wallMinute ||
            (candidate.wallMinute === currentBest.wallMinute && candidate.instant < currentBest.instant)
              ? candidate
              : currentBest,
          firstPostGapMatch,
        ).instant;

  return { exactInstants, postGapInstant };
}

/**
 * Materialize a wall-clock (`y-m-d hour:minute:00`) in `zone` to an absolute instant, resolving DST.
 *
 * Spring-forward gap (the wall-clock doesn't exist): no offset maps back to the requested local hour.
 * We roll FORWARD to the gap's far side at the same minute so the run fires once, immediately after
 * the transition — never skipped, never thrown.
 *
 * Fall-back fold (the wall-clock occurs twice): multiple offsets map back to the requested wall-clock.
 * For day/week/month cadences, keep the earliest instant so a civil slot fires once. Hourly recurrence
 * uses `materializeHourlyInstants` below so both repeated top-of-hour instants are schedulable.
 */
function materialize(year: number, month0: number, day: number, hour: number, minute: number, zone: string): Date {
  const resolved = resolveWallTime(year, month0, day, hour, minute, zone);
  const [instant] = resolved.exactInstants;
  if (instant !== undefined) return new Date(instant);
  if (resolved.postGapInstant !== null) return new Date(resolved.postGapInstant);

  throw noValidInstantError(year, month0, day, hour, minute, zone);
}

/**
 * Hourly recurrence has to see every exact fold occurrence: 01:00 happens twice on fall-back days.
 * Gap slots still collapse to the first valid post-gap instant, matching `materialize`.
 */
function materializeHourlyInstants(
  year: number,
  month0: number,
  day: number,
  hour: number,
  minute: number,
  zone: string,
): number[] {
  const resolved = resolveWallTime(year, month0, day, hour, minute, zone);
  if (resolved.exactInstants.length > 0) return resolved.exactInstants;
  if (resolved.postGapInstant !== null) return [resolved.postGapInstant];

  throw noValidInstantError(year, month0, day, hour, minute, zone);
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}
function pad4(n: number): string {
  return String(n).padStart(4, '0');
}

/**
 * Advance a schedule-zone calendar date without consulting the host timezone.
 *
 * Dayjs timezone instances preserve/refresh offsets in ways that can depend on the process-local TZ
 * when mutated across DST boundaries. Recurrence scanning needs only the civil date in the schedule's
 * own zone, so normalize through UTC calendar fields and let `materialize` resolve each wall slot.
 */
function addCalendarDays({ year, month0, day }: LocalDateParts, deltaDays: number): LocalDateParts {
  const d = new Date(Date.UTC(year, month0, day + deltaDays));
  return { year: d.getUTCFullYear(), month0: d.getUTCMonth(), day: d.getUTCDate() };
}

function addLocalHours(
  { year, month0, day }: LocalDateParts,
  hour: number,
  deltaHours: number,
): LocalDateParts & { hour: number } {
  const totalHours = hour + deltaHours;
  const dayDelta = Math.floor(totalHours / 24);
  const nextHour = ((totalHours % 24) + 24) % 24;
  return { ...addCalendarDays({ year, month0, day }, dayDelta), hour: nextHour };
}

/**
 * The first valid fire instant STRICTLY AFTER `from`, in the spec's zone. Returns an absolute Date.
 */
export function nextFire(spec: RecurringSpec, from: Date): Date {
  if (spec.cadence === 'hourly') {
    // Next top-of-the-local-hour (`:minute:00`) strictly after `from`, computed in spec.timezone —
    // NOT the server's local zone. For non-integer-offset zones (e.g. Asia/Kolkata +05:30) the top
    // of a local hour does NOT align with a server-local minute boundary: anchoring on the wall
    // clock in the wrong zone returns `:30`-offset instants; anchoring through Intl keeps this tied
    // to the schedule's zone rather than the host zone. Repeated fall-back hours produce two exact
    // instants, so scan the current local hour before advancing the civil hour.
    const local = localParts(from, spec.timezone);
    for (const instant of materializeHourlyInstants(
      local.year,
      local.month0,
      local.day,
      local.hour,
      spec.minute,
      spec.timezone,
    )) {
      if (instant > from.getTime()) return new Date(instant);
    }

    const next = addLocalHours(local, local.hour, 1);
    const [nextHourInstant] = materializeHourlyInstants(
      next.year,
      next.month0,
      next.day,
      next.hour,
      spec.minute,
      spec.timezone,
    );
    if (nextHourInstant === undefined) {
      throw noValidInstantError(next.year, next.month0, next.day, next.hour, spec.minute, spec.timezone);
    }
    return new Date(nextHourInstant);
  }

  // daily / weekdays / weekly / monthly: walk schedule-zone calendar dates until one is strictly
  // after `from`. Keep the probe as plain date parts so host-local timezone settings cannot leak into
  // recurrence advancement across DST boundaries; `materialize` is the single place that resolves a
  // wall-clock slot into an absolute instant.
  const local = localParts(from, spec.timezone);
  let probe: LocalDateParts = { year: local.year, month0: local.month0, day: local.day };
  // Cap the scan: monthly needs at most ~32 day-steps; the others far fewer. 400 is generous and
  // guarantees termination even across a month boundary in any zone.
  for (let i = 0; i < 400; i++) {
    if (periodMatchesDate(spec, probe)) {
      const fire = materialize(probe.year, probe.month0, probe.day, spec.hour, spec.minute, spec.timezone);
      if (fire.getTime() > from.getTime()) {
        return fire;
      }
    }
    probe = addCalendarDays(probe, 1);
  }
  throw new Error(`nextFire: no matching instant found within scan window for ${spec.cadence}`);
}

/**
 * The latest valid fire instant AT OR BEFORE `from`. Used only to seed the first-run / manual
 * lower bound and for previews — the live system reads the previous slot from the ledger (§2.2).
 */
export function prevFire(spec: RecurringSpec, from: Date): Date {
  if (spec.cadence === 'hourly') {
    // Latest top-of-the-local-hour at or before `from`, anchored in spec.timezone (see nextFire).
    // Repeated fall-back hours are both valid hourly slots; choose the latest exact occurrence that
    // is not after `from` before moving to the previous civil hour.
    const local = localParts(from, spec.timezone);
    const currentHourInstants = materializeHourlyInstants(
      local.year,
      local.month0,
      local.day,
      local.hour,
      spec.minute,
      spec.timezone,
    );
    for (let i = currentHourInstants.length - 1; i >= 0; i--) {
      const instant = currentHourInstants[i];
      if (instant !== undefined && instant <= from.getTime()) return new Date(instant);
    }

    const prev = addLocalHours(local, local.hour, -1);
    const prevHourInstants = materializeHourlyInstants(
      prev.year,
      prev.month0,
      prev.day,
      prev.hour,
      spec.minute,
      spec.timezone,
    );
    const prevHourInstant = prevHourInstants[prevHourInstants.length - 1];
    if (prevHourInstant === undefined) {
      throw noValidInstantError(prev.year, prev.month0, prev.day, prev.hour, spec.minute, spec.timezone);
    }
    return new Date(prevHourInstant);
  }

  const local = localParts(from, spec.timezone);
  let probe: LocalDateParts = { year: local.year, month0: local.month0, day: local.day };
  for (let i = 0; i < 400; i++) {
    if (periodMatchesDate(spec, probe)) {
      const fire = materialize(probe.year, probe.month0, probe.day, spec.hour, spec.minute, spec.timezone);
      if (fire.getTime() <= from.getTime()) {
        return fire;
      }
    }
    probe = addCalendarDays(probe, -1);
  }
  throw new Error(`prevFire: no matching instant found within scan window for ${spec.cadence}`);
}

/**
 * Does the intended schedule-zone date match the cadence's period constraint? The resolved fire
 * instant can land on the following local date when a spring-forward gap skips the final hour of a
 * civil day, but the recurrence still belongs to the probed wall-calendar slot.
 */
function periodMatchesDate(spec: RecurringSpec, local: LocalDateParts): boolean {
  switch (spec.cadence) {
    case 'daily':
      return true;
    case 'weekdays': {
      const dow = new Date(Date.UTC(local.year, local.month0, local.day)).getUTCDay(); // Sun=0 .. Sat=6
      return dow >= MONDAY && dow <= 5;
    }
    case 'weekly':
      return new Date(Date.UTC(local.year, local.month0, local.day)).getUTCDay() === MONDAY;
    case 'monthly':
      return local.day === 1;
    case 'hourly':
      return true;
  }
}

/**
 * Compute the half-open `[start, end)` window for a recurring run.
 *
 * @param thisSlot     the `scheduled_for` of the firing slot (= range_end).
 * @param previousSlot the `scheduled_for` of the prior slot for this workflow, or null on first run.
 * @param spec         recurrence — used ONLY to synthesize the first-run lower bound.
 *
 * - Normal run: `{ start: previousSlot, end: thisSlot }` — pure ledger read, no clock math.
 * - First run (previousSlot === null): synthesize `start` as one cadence period before `thisSlot`
 *   (`prevFire(spec, thisSlot - 1ms)`), so e.g. a daily workflow's first run is a 24h window, not
 *   all-history. (Alternative "since createdAt / epoch" rejected: makes the first message unbounded.)
 */
export function computeWindow(thisSlot: Date, previousSlot: Date | null, spec: RecurringSpec): TriggerWindow {
  if (previousSlot !== null) {
    return { start: previousSlot, end: thisSlot };
  }
  // No prior fire: the most recent fire strictly before thisSlot (one cadence period back).
  const start = prevFire(spec, new Date(thisSlot.getTime() - 1));
  return { start, end: thisSlot };
}

/**
 * Compute the window for a manual "Run now" / dry-run on a RECURRING workflow: the most recent
 * COMPLETE schedule period — exactly what the latest scheduled fire would have computed. The most
 * recent cron instant at-or-before `now` is the window end (exclusive); the prior instant is the
 * start: `[prevFire(prevFire(now)), prevFire(now))`. This keeps "Run now" and the dry-run preview
 * consistent with a real scheduled run (same `computeWindow` semantics).
 *
 * Previously this returned `[prevFire(now), now]` — an intra-period PARTIAL window from the last fire
 * to now. For a daily routine over day-keyed aggregates that window sits entirely within TODAY, so a
 * `day >= {{range_start}}::date AND day < {{range_end}}::date` filter collapsed to an empty same-day
 * range (start::date === end::date) and returned zero rows — the preview/run-now never matched what a
 * scheduled fire reports (the previous, completed day).
 *
 * A pure-`manual` workflow (no cadence) has no window — using `{{range_*}}` there is a save-time
 * validation error (part 8), so this helper is never called for one.
 */
export function manualWindow(spec: RecurringSpec, now: Date): TriggerWindow {
  const end = prevFire(spec, now);
  const start = prevFire(spec, new Date(end.getTime() - 1));
  return { start, end };
}

/**
 * Default `[now - 1h, now)` window for a workflow with NO cadence (a pure-`manual` trigger, or no
 * trigger at all — the draft preview). There is no schedule period to derive a span from, so we fall
 * back to a fixed one-hour lookback.
 *
 * SINGLE SOURCE OF TRUTH: both the dry-run preview (`defaultPreviewWindow`) and the live executor
 * (`computeRunWindow`) call this for the no-cadence case, so a pure-manual `{{range_*}}` query binds
 * the SAME window in the preview and the live run — they can never drift. (Previously the executor
 * supplied NO window for a manual run, throwing "no trigger window was supplied" live despite a green
 * preview.)
 */
const DEFAULT_MANUAL_LOOKBACK_MS = 60 * 60 * 1000; // 1 hour
export function defaultManualWindow(now: Date): TriggerWindow {
  return { start: new Date(now.getTime() - DEFAULT_MANUAL_LOOKBACK_MS), end: now };
}
